import { useState, useEffect } from 'react';
import { ArrowLeft, Check, AlertTriangle, FileText, Calendar, User, FolderOpen, Edit2, AlertCircle, CalendarDays } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { parseDocument, getOverallConfidence } from '@/services/documentParserService';
import { useHolidays } from '@/hooks/useHolidays';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import type { ExtractedDocumentData, DeadlineCategory, DeadlinePriority, DeadlineDayType } from '@/types/deadlines';

interface ExtractionResultsFormProps {
  file: File;
  extractedText: string;
  onSubmit: (data: ExtractionFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export interface ExtractionFormData {
  expedienteNumber: string;
  title: string;
  description: string;
  interestedParty: string;
  dueDate: string;
  originalDueDate: string;
  category: DeadlineCategory;
  priority: DeadlinePriority;
  dayType: DeadlineDayType;
  reminderDays: number[];
  documentPath?: string;
}

const CATEGORY_LABELS: Record<DeadlineCategory, string> = {
  urbanismo: 'Urbanismo',
  contratacion: 'Contratación',
  hacienda: 'Hacienda',
  personal: 'Personal',
  otros: 'Otros',
};

const PRIORITY_LABELS: Record<DeadlinePriority, string> = {
  alta: 'Alta',
  media: 'Media',
  baja: 'Baja',
};

const DAY_TYPE_LABELS: Record<DeadlineDayType, string> = {
  habiles: 'Días hábiles',
  naturales: 'Días naturales',
  fecha_fija: 'Fecha fija',
};

const DEFAULT_REMINDER_DAYS = [7, 3, 1, 0];

export function ExtractionResultsForm({
  file,
  extractedText,
  onSubmit,
  onCancel,
  isLoading = false,
}: ExtractionResultsFormProps) {
  const { calculateDeadlineDate, autonomousCommunity } = useHolidays();
  const [extractedData, setExtractedData] = useState<ExtractedDocumentData | null>(null);
  const [overallConfidence, setOverallConfidence] = useState(0);
  
  // Form state
  const [expedienteNumber, setExpedienteNumber] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [interestedParty, setInterestedParty] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [originalDueDate, setOriginalDueDate] = useState('');
  const [category, setCategory] = useState<DeadlineCategory>('otros');
  const [priority, setPriority] = useState<DeadlinePriority>('media');
  const [dayType, setDayType] = useState<DeadlineDayType>('habiles');
  const [reminderDays, setReminderDays] = useState<number[]>(DEFAULT_REMINDER_DAYS);
  
  // Date calculation state
  const [wasAdjusted, setWasAdjusted] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);

  // Parse document and calculate dates on mount
  useEffect(() => {
    const initializeForm = async () => {
      const data = parseDocument(extractedText);
      setExtractedData(data);
      setOverallConfidence(getOverallConfidence(data));
      
      // Pre-fill form with extracted data
      if (data.expedienteNumber) setExpedienteNumber(data.expedienteNumber);
      if (data.title) setTitle(data.title);
      if (data.interestedParty) setInterestedParty(data.interestedParty);
      if (data.category) setCategory(data.category);
      if (data.deadlineType) {
        setDayType(data.deadlineType === 'naturales' ? 'naturales' : 'habiles');
      }

      // Calculate due date with holidays
      if (data.dueDate) {
        // Si ya hay fecha límite detectada directamente, usarla
        setDueDate(format(data.dueDate, 'yyyy-MM-dd'));
        setOriginalDueDate(format(data.dueDate, 'yyyy-MM-dd'));
      } else if (data.documentDate && data.deadlineDays) {
        // Si hay fecha documento y plazo, calcular con festivos
        setIsCalculating(true);
        try {
          const deadlineType = data.deadlineType === 'naturales' ? 'naturales' : 'habiles';
          const result = await calculateDeadlineDate(
            data.documentDate,
            data.deadlineDays,
            deadlineType
          );
          setDueDate(format(result.dueDate, 'yyyy-MM-dd'));
          setOriginalDueDate(format(result.originalDueDate, 'yyyy-MM-dd'));
          setWasAdjusted(result.wasAdjusted);
        } catch (error) {
          console.error('Error calculating deadline:', error);
          // Fallback: cálculo simple sin festivos
          const fallbackDate = new Date(data.documentDate);
          fallbackDate.setDate(fallbackDate.getDate() + data.deadlineDays);
          setDueDate(format(fallbackDate, 'yyyy-MM-dd'));
          setOriginalDueDate(format(fallbackDate, 'yyyy-MM-dd'));
        } finally {
          setIsCalculating(false);
        }
      }
    };

    initializeForm();
  }, [extractedText, calculateDeadlineDate]);

  const handleReminderToggle = (day: number) => {
    setReminderDays(prev => 
      prev.includes(day) 
        ? prev.filter(d => d !== day)
        : [...prev, day].sort((a, b) => b - a)
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim() || !dueDate) {
      return;
    }
    
    onSubmit({
      expedienteNumber,
      title,
      description,
      interestedParty,
      dueDate,
      originalDueDate: originalDueDate || dueDate,
      category,
      priority,
      dayType,
      reminderDays,
    });
  };

  const getConfidenceBadge = (confidence: number) => {
    if (confidence >= 0.8) {
      return <Badge variant="default" className="bg-deadline-safe text-deadline-safe-foreground">✓ Detectado</Badge>;
    } else if (confidence >= 0.5) {
      return <Badge variant="secondary" className="bg-deadline-soon text-deadline-soon-foreground">Revisar</Badge>;
    }
    return null;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Volver
        </Button>
      </div>

      {/* Archivo procesado */}
      <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
        <FileText className="h-8 w-8 text-primary" />
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{file.name}</p>
          <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
        </div>
        {overallConfidence > 0 && (
          <Badge 
            variant={overallConfidence >= 0.6 ? 'default' : 'secondary'}
            className={overallConfidence >= 0.6 ? 'bg-deadline-safe text-deadline-safe-foreground' : 'bg-deadline-soon text-deadline-soon-foreground'}
          >
            {Math.round(overallConfidence * 100)}% confianza
          </Badge>
        )}
      </div>

      {/* Sección: Datos del expediente */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <FolderOpen className="h-4 w-4" />
          Datos del expediente
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="expediente">Número de expediente</Label>
              {extractedData && getConfidenceBadge(extractedData.confidence.expediente)}
            </div>
            <Input
              id="expediente"
              value={expedienteNumber}
              onChange={(e) => setExpedienteNumber(e.target.value)}
              placeholder="Ej: 2026/00123"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="category">Categoría</Label>
              {extractedData && getConfidenceBadge(extractedData.confidence.category)}
            </div>
            <Select value={category} onValueChange={(v) => setCategory(v as DeadlineCategory)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="title">Título del plazo *</Label>
          <Input
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ej: Licencia de obra menor - C/ Mayor 15"
            required
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="interested">Interesado / Solicitante</Label>
            {extractedData && getConfidenceBadge(extractedData.confidence.interested)}
          </div>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="interested"
              value={interestedParty}
              onChange={(e) => setInterestedParty(e.target.value)}
              placeholder="Nombre del interesado"
              className="pl-10"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">Descripción</Label>
          <Textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Descripción adicional del expediente..."
            rows={2}
          />
        </div>
      </div>

      {/* Sección: Plazos */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Calendar className="h-4 w-4" />
          Plazos detectados
          {extractedData && getConfidenceBadge(extractedData.confidence.dates)}
        </div>

        {extractedData?.documentDate && (
          <div className="text-sm text-muted-foreground">
            Fecha del documento: {format(extractedData.documentDate, "d 'de' MMMM 'de' yyyy", { locale: es })}
            {extractedData.deadlineDays && (
              <span className="ml-2">
                • Plazo mencionado: {extractedData.deadlineDays} días {extractedData.deadlineType}
              </span>
            )}
          </div>
        )}

        {/* Indicador de cálculo con festivos */}
        {isCalculating ? (
          <div className="p-3 bg-muted rounded-lg animate-pulse">
            <p className="text-sm text-muted-foreground">
              Calculando fecha considerando festivos de {autonomousCommunity === 'andalucia' ? 'Andalucía' : autonomousCommunity === 'extremadura' ? 'Extremadura' : 'ámbito nacional'}...
            </p>
          </div>
        ) : wasAdjusted && originalDueDate && dueDate !== originalDueDate ? (
          <div className="p-3 rounded-lg border bg-deadline-soon/10 border-deadline-soon/30">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-deadline-soon flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-sm text-deadline-soon">
                  Fecha ajustada por festivo
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  La fecha original ({format(new Date(originalDueDate), "d 'de' MMM", { locale: es })}) 
                  cae en festivo o fin de semana. Se ha ajustado al siguiente día hábil.
                </p>
              </div>
            </div>
          </div>
        ) : dueDate && extractedData?.deadlineDays ? (
          <div className="p-3 rounded-lg border bg-deadline-safe/10 border-deadline-safe/30">
            <div className="flex items-start gap-2">
              <CalendarDays className="h-4 w-4 text-deadline-safe flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm">
                  Fecha calculada considerando festivos de {autonomousCommunity === 'andalucia' ? 'Andalucía' : autonomousCommunity === 'extremadura' ? 'Extremadura' : 'ámbito nacional'}
                </p>
              </div>
            </div>
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="dueDate">Fecha límite *</Label>
            <Input
              id="dueDate"
              type="date"
              value={dueDate}
              onChange={(e) => {
                setDueDate(e.target.value);
                // Si el usuario cambia manualmente, ya no está ajustada
                if (e.target.value !== originalDueDate) {
                  setOriginalDueDate(e.target.value);
                  setWasAdjusted(false);
                }
              }}
              required
              disabled={isCalculating}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="dayType">Tipo de días</Label>
            <Select value={dayType} onValueChange={(v) => setDayType(v as DeadlineDayType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(DAY_TYPE_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Sección: Configuración */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Edit2 className="h-4 w-4" />
          Configuración
        </div>

        <div className="space-y-2">
          <Label>Prioridad</Label>
          <Select value={priority} onValueChange={(v) => setPriority(v as DeadlinePriority)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Recordatorios</Label>
          <div className="flex flex-wrap gap-2">
            {[7, 3, 1, 0].map((day) => (
              <label
                key={day}
                className="flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer hover:bg-muted transition-colors"
              >
                <Checkbox
                  checked={reminderDays.includes(day)}
                  onCheckedChange={() => handleReminderToggle(day)}
                />
                <span className="text-sm">
                  {day === 0 ? 'Mismo día' : `${day} día${day > 1 ? 's' : ''}`}
                </span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Aviso de baja confianza */}
      {overallConfidence < 0.5 && (
        <div className="flex items-start gap-2 p-3 bg-deadline-soon/10 border border-deadline-soon/30 rounded-lg">
          <AlertTriangle className="h-5 w-5 text-deadline-soon flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-deadline-soon">
              Extracción con baja confianza
            </p>
            <p className="text-sm text-muted-foreground">
              Revisa y completa los datos manualmente. El documento puede tener un formato no estándar.
            </p>
          </div>
        </div>
      )}

      {/* Botones */}
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" disabled={isLoading || !title.trim() || !dueDate}>
          {isLoading ? (
            <>Creando...</>
          ) : (
            <>
              <Check className="h-4 w-4 mr-2" />
              Crear expediente
            </>
          )}
        </Button>
      </div>
    </form>
  );
}
